import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  PlusIcon, PencilSquareIcon, TrashIcon, ClipboardDocumentIcon,
  CalendarDaysIcon, BoltIcon, DocumentTextIcon, ChartBarIcon, ExclamationTriangleIcon,
  UsersIcon, CheckIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../../components/layout/MainLayout';
import BackButton from '../../components/common/BackButton';
import applicationApi from '../../services/api/application.api';
import { observabilityApi } from '../../services/api/observability.api';

interface AppStat {
  sessions: number;
  logs: number;
  traces: number;
  errors: number;
  total: number;
  loading: boolean;
}

const emptyForm = { name: '', description: '', language: '', framework: '', environment: 'development' as 'development' | 'staging' | 'production' };

export default function ApplicationsPage() {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingApp, setEditingApp] = useState<any | null>(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [stats, setStats] = useState<Record<string, AppStat>>({});

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { loadApplications(); }, []);

  // Abre o modal de criar direto via "/settings/applications?new=1"
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setShowCreateModal(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationApi.getAll();
      if (response.success) {
        setApplications(response.data);
        loadStats(response.data);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dados das últimas 24h por aplicação (pra saber qual está mais ativa)
  const loadStats = (apps: any[]) => {
    const init: Record<string, AppStat> = {};
    apps.forEach((a) => { init[a.id] = { sessions: 0, logs: 0, traces: 0, errors: 0, total: 0, loading: true }; });
    setStats(init);
    apps.forEach(async (app) => {
      try {
        const d: any = await observabilityApi.getDashboard('24h', app.id);
        const sessions = d?.rum?.totalSessions || 0;
        const logs = d?.overview?.logsToday || 0;
        const traces = d?.overview?.tracesToday || 0;
        const errors = d?.rum?.frontendErrors || 0;
        setStats((prev) => ({ ...prev, [app.id]: { sessions, logs, traces, errors, total: sessions + logs + traces, loading: false } }));
      } catch {
        setStats((prev) => ({ ...prev, [app.id]: { sessions: 0, logs: 0, traces: 0, errors: 0, total: 0, loading: false } }));
      }
    });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await applicationApi.create(formData);
      if (response.success) {
        setShowCreateModal(false);
        setFormData({ ...emptyForm });
        loadApplications();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('settings:applications.create.error', 'Falha ao criar aplicação'));
    }
  };

  const openEdit = (app: any) => {
    setEditingApp(app);
    setFormData({
      name: app.name || '',
      description: app.description || '',
      language: app.language || '',
      framework: app.framework || '',
      environment: app.environment || 'development',
    });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingApp) return;
    try {
      const response = await applicationApi.update(editingApp.id, formData);
      if (response.success) {
        toast.success(t('settings:applications.updated', 'Aplicação atualizada!'));
        setEditingApp(null);
        setFormData({ ...emptyForm });
        loadApplications();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('settings:applications.updateError', 'Falha ao atualizar'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('settings:applications.deleteConfirm'))) return;
    try {
      await applicationApi.delete(id);
      loadApplications();
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const copy = (text: string, msg: string) => { navigator.clipboard.writeText(text); toast.success(msg); };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(i18n.language || 'pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso.substring(0, 10); }
  };

  const formatNum = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

  const envStyle = (env: string) => {
    switch (env) {
      case 'production': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      case 'staging': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      default: return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300';
    }
  };
  const envTile = (env: string) => {
    switch (env) {
      case 'production': return 'from-red-500 to-rose-600';
      case 'staging': return 'from-amber-500 to-orange-600';
      default: return 'from-sky-500 to-blue-600';
    }
  };

  // App com mais dados nas últimas 24h
  const maxTotal = Math.max(0, ...applications.map((a) => stats[a.id]?.total || 0));

  const StatPill = ({ icon: Icon, label, value, accent }: any) => (
    <div className="flex flex-col items-center rounded-lg bg-gray-50 px-2 py-2 dark:bg-gray-800/60">
      <Icon className={`h-4 w-4 ${accent}`} />
      <span className="mt-1 text-sm font-bold text-gray-900 dark:text-white">{value}</span>
      <span className="text-[10px] text-gray-500 dark:text-gray-400">{label}</span>
    </div>
  );

  return (
    <MainLayout>
      <BackButton />
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">{t('settings:applications.title')}</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t('settings:applications.subtitle')}</p>
        </div>
        <button onClick={() => { setFormData({ ...emptyForm }); setShowCreateModal(true); }} className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700">
          <PlusIcon className="h-5 w-5" />
          {t('settings:applications.createApp')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600"></div>
          </div>
        ) : applications.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 py-16 text-center dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">{t('settings:applications.noApplications')}</p>
            <button onClick={() => { setFormData({ ...emptyForm }); setShowCreateModal(true); }} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
              <PlusIcon className="h-4 w-4" /> {t('settings:applications.createApp')}
            </button>
          </div>
        ) : (
          applications.map((app) => {
            const s = stats[app.id];
            const isTop = !!s && !s.loading && s.total > 0 && s.total === maxTotal && applications.length > 1;
            return (
              <div key={app.id} className="flex flex-col rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-900">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 p-5 pb-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${envTile(app.environment)} text-lg font-bold text-white shadow-sm`}>
                      {(app.name?.[0] || '?').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate text-base font-semibold text-gray-900 dark:text-white">{app.name}</h3>
                        {isTop && (
                          <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            {t('settings:applications.mostActive', 'Mais ativa')}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${envStyle(app.environment)}`}>
                          {t(`settings:applications.environments.${app.environment}`)}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                          <CalendarDaysIcon className="h-3.5 w-3.5" /> {formatDate(app.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button onClick={() => openEdit(app)} title={t('settings:applications.edit', 'Editar')} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-indigo-600 dark:hover:bg-gray-800 dark:hover:text-indigo-400">
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(app.id)} title={t('settings:applications.delete')} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {app.description && (
                  <p className="px-5 pb-3 text-sm text-gray-500 line-clamp-2 dark:text-gray-400">{app.description}</p>
                )}

                {/* Stats 24h */}
                <div className="px-5">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('settings:applications.last24h', 'Últimas 24h')}</span>
                    {s?.loading && <span className="h-3 w-3 animate-spin rounded-full border-b-2 border-indigo-500" />}
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <StatPill icon={UsersIcon} label={t('settings:applications.sessions', 'Sessões')} value={formatNum(s?.sessions || 0)} accent="text-purple-500" />
                    <StatPill icon={DocumentTextIcon} label={t('settings:applications.logs', 'Logs')} value={formatNum(s?.logs || 0)} accent="text-green-500" />
                    <StatPill icon={ChartBarIcon} label={t('settings:applications.traces', 'Traces')} value={formatNum(s?.traces || 0)} accent="text-blue-500" />
                    <StatPill icon={ExclamationTriangleIcon} label={t('settings:applications.errors', 'Erros')} value={formatNum(s?.errors || 0)} accent="text-red-500" />
                  </div>
                </div>

                {/* Credenciais */}
                <div className="mt-4 space-y-2 border-t border-gray-100 p-5 pt-4 dark:border-gray-800">
                  {[
                    { label: t('settings:applications.appId', 'App ID'), value: app.id, msg: t('settings:applications.appIdCopied', 'App ID copiado!'), mono: app.id },
                    { label: t('settings:applications.apiKey'), value: app.api_key, msg: t('settings:applications.apiKeyCopied', 'API Key copiada!'), mono: `${app.api_key?.substring(0, 16)}…` },
                    ...(app.public_api_key ? [{ label: t('settings:applications.publicApiKey', 'Public Key'), value: app.public_api_key, msg: t('settings:applications.publicKeyCopied', 'Public Key copiada!'), mono: `${app.public_api_key.substring(0, 16)}…` }] : []),
                  ].map((c) => (
                    <div key={c.label} className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/60">
                      <div className="min-w-0">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{c.label}</p>
                        <p className="truncate font-mono text-xs text-gray-600 dark:text-gray-300">{c.mono}</p>
                      </div>
                      <button onClick={() => copy(c.value, c.msg)} title={t('common:buttons.copy', 'Copiar')} className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-200 hover:text-indigo-600 dark:hover:bg-gray-700 dark:hover:text-indigo-400">
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal criar/editar */}
      {(showCreateModal || editingApp) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setShowCreateModal(false); setEditingApp(null); }}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
            <h2 className="mb-5 text-xl font-bold text-gray-900 dark:text-white">
              {editingApp ? t('settings:applications.editTitle', 'Editar aplicação') : t('settings:applications.create.title')}
            </h2>
            <form onSubmit={editingApp ? handleUpdate : handleCreate} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings:applications.create.name')} *</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" autoFocus />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings:applications.create.description')}</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings:applications.create.language')} {!editingApp && '*'}</label>
                  <input type="text" required={!editingApp} value={formData.language} onChange={(e) => setFormData({ ...formData, language: e.target.value })} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" placeholder="JavaScript…" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings:applications.create.framework')}</label>
                  <input type="text" value={formData.framework} onChange={(e) => setFormData({ ...formData, framework: e.target.value })} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" placeholder="Node.js…" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings:applications.create.environment')} *</label>
                <select required value={formData.environment} onChange={(e) => setFormData({ ...formData, environment: e.target.value as any })} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                  <option value="development">{t('settings:applications.environments.development')}</option>
                  <option value="staging">{t('settings:applications.environments.staging')}</option>
                  <option value="production">{t('settings:applications.environments.production')}</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreateModal(false); setEditingApp(null); }} className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                  {t('settings:applications.create.cancel')}
                </button>
                <button type="submit" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
                  <CheckIcon className="h-4 w-4" />
                  {editingApp ? t('common:buttons.save', 'Salvar') : t('settings:applications.create.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
